import React, { useEffect } from 'react';
import { FormControl, Button, Grid, Paper, Typography, OutlinedInput, Box, FormLabel, FormHelperText, MenuItem, Select, Divider, IconButton, Chip, Switch, FormControlLabel } from '@mui/material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { postLabTestRequest, putLabTestRequest, resetPostLabTest, resetPutLabTest, getLabTestFormulasRequest } from '../../../../../redux/modules/laboratory/labTest/slice';
import { getLabDepartmentRequest } from '../../../../../redux/modules/laboratory/labDepartment/slice';
import { getLabSpecimenTypeRequest } from '../../../../../redux/modules/laboratory/labSpecimenType/slice';

const validationSchema = yup.object().shape({
    testCode: yup.string().required("Test code is required"),
    name: yup.string().required("Name is required"),
    resultType: yup.string().required("Result type is required"),
    departmentId: yup.string().required("Department is required"),
    specimenTypeId: yup.string().nullable(),
    unit: yup.string().nullable(),
    decimalPlaces: yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
    price: yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
    expectedTATMinutes: yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
});

const resultTypes = [
    { value: 'numeric', label: 'Numeric' },
    { value: 'text', label: 'Text' },
    { value: 'coded', label: 'Coded (e.g. Positive/Negative)' },
    { value: 'multiline', label: 'Multiline Text' },
    { value: 'calculated', label: 'Calculated' },
];

const Form = ({ itemData, toggleList }) => {
    const dispatch = useDispatch();
    let { loading, postRes, putRes } = useSelector(state => state.labTest);
    const departments = useSelector(state => state.labDepartment.getRes?.data);
    const specimenTypes = useSelector(state => state.labSpecimenType.getRes?.data);
    const formulas = useSelector(state => state.labTest.formulasRes?.data);

    const { register, handleSubmit, control, watch, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            ...itemData,
            departmentId: itemData?.departmentId?._id || itemData?.departmentId || '',
            specimenTypeId: itemData?.specimenTypeId?._id || itemData?.specimenTypeId || '',
            resultType: itemData?.resultType || 'numeric',
            decimalPlaces: itemData?.decimalPlaces ?? 1,
            expectedTATMinutes: itemData?.expectedTATMinutes ?? 120,
            isOrderable: itemData?.isOrderable !== false,
            referenceRanges: itemData?.referenceRanges?.length > 0 ? itemData.referenceRanges : [{ label: '', gender: 'all', ageMin: '', ageMax: '', lowNormal: '', highNormal: '', lowCritical: '', highCritical: '', textNormal: '' }],
            // useFieldArray needs objects, so the codedValues string array is
            // wrapped as {value} rows here and unwrapped again on submit.
            codedOptions: itemData?.codedValues?.length > 0 ? itemData.codedValues.map(v => ({ value: v })) : [{ value: '' }],
        }
    });

    const { fields, append, remove } = useFieldArray({ control, name: 'referenceRanges' });
    const { fields: optionFields, append: appendOption, remove: removeOption } = useFieldArray({ control, name: 'codedOptions' });
    const watchResultType = watch('resultType');

    useEffect(() => {
        dispatch(getLabDepartmentRequest({ page: 1, limit: 100, searchQuery: '' }));
        dispatch(getLabSpecimenTypeRequest({ page: 1, limit: 100, searchQuery: '' }));
        dispatch(getLabTestFormulasRequest());
    }, [dispatch]);

    useEffect(() => {
        if (postRes) {
            if (postRes?.success) { toggleList(); }
            toast(postRes?.message);
            dispatch(resetPostLabTest());
        }
        if (putRes) {
            if (putRes?.success) { toggleList(); }
            toast(putRes?.message);
            dispatch(resetPutLabTest());
        }
    }, [postRes, putRes, toggleList, dispatch]);

    const onSubmit = (data) => {
        const { codedOptions, ...payload } = data;
        if (payload.resultType === 'coded') {
            const opts = (codedOptions || []).map(o => (o?.value || '').trim()).filter(Boolean);
            payload.codedValues = [...new Set(opts)];
            if (payload.codedValues.length === 0) {
                toast('Add at least one coded option — these are the dropdown choices on the entry screen');
                return;
            }
        }
        // Non-coded types don't touch codedValues: the stored options survive a
        // temporary type switch instead of being wiped by the round-trip.
        if (itemData?._id) {
            dispatch(putLabTestRequest(payload));
        } else {
            dispatch(postLabTestRequest(payload));
        }
    };

    return (
        <Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
            <Paper sx={{ padding: 2, mt: 3 }} className='cardStyle'>
                <Typography variant="h5" className='title'>Test Definition Form</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Test Code</FormLabel>
                            <OutlinedInput className="inputfield" {...register('testCode')} size='small' placeholder="e.g. GLU" />
                            {errors?.testCode?.message && <FormHelperText error>{errors?.testCode?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Test Name</FormLabel>
                            <OutlinedInput className="inputfield" {...register('name')} size='small' placeholder="e.g. Glucose, Fasting" />
                            {errors?.name?.message && <FormHelperText error>{errors?.name?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Short Name</FormLabel>
                            <OutlinedInput className="inputfield" {...register('shortName')} size='small' placeholder="e.g. FBS" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Department</FormLabel>
                            <Controller
                                name="departmentId"
                                control={control}
                                render={({ field }) => (
                                    <Select {...field} size="small" displayEmpty>
                                        <MenuItem value="">Select department</MenuItem>
                                        {departments?.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                                    </Select>
                                )}
                            />
                            {errors?.departmentId?.message && <FormHelperText error>{errors?.departmentId?.message}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Specimen Type</FormLabel>
                            <Controller
                                name="specimenTypeId"
                                control={control}
                                render={({ field }) => (
                                    <Select {...field} size="small" displayEmpty>
                                        <MenuItem value="">Select specimen</MenuItem>
                                        {specimenTypes?.map(s => <MenuItem key={s._id} value={s._id}>{s.name} ({s.code})</MenuItem>)}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Result Type</FormLabel>
                            <Controller
                                name="resultType"
                                control={control}
                                render={({ field }) => (
                                    <Select {...field} size="small">
                                        {resultTypes.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    {(watchResultType === 'numeric' || watchResultType === 'calculated') && (
                        <>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth>
                                    <FormLabel>Unit</FormLabel>
                                    <OutlinedInput className="inputfield" {...register('unit')} size='small' placeholder="e.g. mg/dL" />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth>
                                    <FormLabel>Decimal Places</FormLabel>
                                    <OutlinedInput className="inputfield" {...register('decimalPlaces')} size='small' type="number" />
                                </FormControl>
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>LOINC Code</FormLabel>
                            <OutlinedInput className="inputfield" {...register('loincCode')} size='small' placeholder="Optional" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Price (GYD)</FormLabel>
                            <OutlinedInput className="inputfield" {...register('price')} size='small' type="number" placeholder="0" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Expected TAT (minutes)</FormLabel>
                            <OutlinedInput className="inputfield" {...register('expectedTATMinutes')} size='small' type="number" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Analyzer Test Code</FormLabel>
                            <OutlinedInput className="inputfield" {...register('analyzerTestCode')} size='small' placeholder="Analyzer's code" />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Orderable</FormLabel>
                            <Controller
                                name="isOrderable"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                                        label={field.value ? 'Yes — appears in the order menu' : 'No — hidden from ordering'}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Coded Options — the dropdown choices on the result-entry screen */}
            {watchResultType === 'coded' && (
                <Paper sx={{ padding: 2, mt: 3 }} className='cardStyle'>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" className='title' sx={{ mb: 0 }}>Coded Options</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => appendOption({ value: '' })}>
                            Add Option
                        </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The choices offered in the result-entry dropdown, in this order — e.g. “NEGATIVE”, “TRACE (5 mg/dL)”, “POSITIVE”. Removing an option never changes results already entered with it.
                    </Typography>
                    <Grid container spacing={1}>
                        {optionFields.map((field, index) => (
                            <Grid item xs={12} sm={6} key={field.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <OutlinedInput className="inputfield" fullWidth {...register(`codedOptions.${index}.value`)} size='small' placeholder={`Option ${index + 1}`} />
                                    <IconButton color="error" size="small" onClick={() => removeOption(index)} disabled={optionFields.length === 1}>
                                        <RemoveCircleOutlineIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}

            {/* Reference Ranges */}
            {watchResultType === 'numeric' && (
                <Paper sx={{ padding: 2, mt: 3 }} className='cardStyle'>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" className='title' sx={{ mb: 0 }}>Reference Ranges</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => append({ label: '', gender: 'all', ageMin: '', ageMax: '', lowNormal: '', highNormal: '', lowCritical: '', highCritical: '', textNormal: '' })}>
                            Add Range
                        </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add a range per age/sex band. Give each a <strong>Label</strong> (e.g. “Adult Male”, “Pregnancy T1”) — labels show in the Results range picker. Leave age blank for all ages, and use <strong>Text Normal</strong> for prose ranges (e.g. “&lt; 5.0”).
                    </Typography>
                    {fields.map((field, index) => (
                        <Box key={field.id}>
                            {index > 0 && <Divider sx={{ my: 2 }} />}
                            <Grid container spacing={2} alignItems="flex-end">
                                <input type="hidden" {...register(`referenceRanges.${index}._id`)} />
                                <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth>
                                        <FormLabel>Label</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.label`)} size='small' placeholder="e.g. Adult Male" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth>
                                        <FormLabel>Gender</FormLabel>
                                        <Controller
                                            name={`referenceRanges.${index}.gender`}
                                            control={control}
                                            render={({ field }) => (
                                                <Select {...field} size="small">
                                                    <MenuItem value="all">All</MenuItem>
                                                    <MenuItem value="male">Male</MenuItem>
                                                    <MenuItem value="female">Female</MenuItem>
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth>
                                        <FormLabel>Age Min</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.ageMin`)} size='small' type="number" placeholder="yrs" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth>
                                        <FormLabel>Age Max</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.ageMax`)} size='small' type="number" placeholder="yrs" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={3} sx={{ textAlign: 'right' }}>
                                    {fields.length > 1 && (
                                        <IconButton color="error" onClick={() => remove(index)}>
                                            <RemoveCircleOutlineIcon />
                                        </IconButton>
                                    )}
                                </Grid>

                                {/* Decimal-friendly: plain text inputs with a decimal keypad hint.
                                    A native type="number" rejected decimals like 0.4 on some
                                    browsers/locales ("please enter a valid value, nearest are…"),
                                    so ranges couldn't be saved. The server coerces these strings
                                    to Number (normalizeReferenceRanges). */}
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth>
                                        <FormLabel>Low Normal</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.lowNormal`)} size='small' inputProps={{ inputMode: 'decimal' }} placeholder="e.g. 0.4" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth>
                                        <FormLabel>High Normal</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.highNormal`)} size='small' inputProps={{ inputMode: 'decimal' }} placeholder="e.g. 1.4" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth>
                                        <FormLabel>Low Critical</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.lowCritical`)} size='small' inputProps={{ inputMode: 'decimal' }} placeholder="optional" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth>
                                        <FormLabel>High Critical</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.highCritical`)} size='small' inputProps={{ inputMode: 'decimal' }} placeholder="optional" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <FormLabel>Text Normal (prose)</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.textNormal`)} size='small' placeholder="e.g. < 5.0 ng/mL" />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    ))}
                </Paper>
            )}

            {/* Reference Range — coded / text / multiline tests (prose/qualitative normal) */}
            {['coded', 'text', 'multiline'].includes(watchResultType) && (
                <Paper sx={{ padding: 2, mt: 3 }} className='cardStyle'>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" className='title' sx={{ mb: 0 }}>Reference Range</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => append({ label: '', gender: 'all', ageMin: '', ageMax: '', lowNormal: '', highNormal: '', lowCritical: '', highCritical: '', textNormal: '' })}>
                            Add Range
                        </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Shown in the report’s Reference column for this test — the expected normal as free text (e.g. “NEGATIVE”, “NOT DETECTED”, “NON-REACTIVE”, or any prose). Leave blank for no printed range. Add a second row with a gender only if the normal differs by sex.
                    </Typography>
                    {fields.map((field, index) => (
                        <Box key={field.id}>
                            {index > 0 && <Divider sx={{ my: 2 }} />}
                            <Grid container spacing={2} alignItems="flex-end">
                                <input type="hidden" {...register(`referenceRanges.${index}._id`)} />
                                <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth>
                                        <FormLabel>Label</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.label`)} size='small' placeholder="Optional, e.g. Adult" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <FormControl fullWidth>
                                        <FormLabel>Gender</FormLabel>
                                        <Controller
                                            name={`referenceRanges.${index}.gender`}
                                            control={control}
                                            render={({ field }) => (
                                                <Select {...field} size="small">
                                                    <MenuItem value="all">All</MenuItem>
                                                    <MenuItem value="male">Male</MenuItem>
                                                    <MenuItem value="female">Female</MenuItem>
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <FormLabel>Reference Range (Normal)</FormLabel>
                                        <OutlinedInput className="inputfield" {...register(`referenceRanges.${index}.textNormal`)} size='small' placeholder="e.g. NEGATIVE" />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                                    {fields.length > 1 && (
                                        <IconButton color="error" onClick={() => remove(index)}>
                                            <RemoveCircleOutlineIcon />
                                        </IconButton>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    ))}
                </Paper>
            )}

            {/* Formula Information (read-only) */}
            {watchResultType === 'calculated' && (() => {
                const testCode = watch('testCode')?.toUpperCase();
                const formulaInfo = testCode && formulas ? formulas[testCode] : null;
                return (
                    <Paper sx={{ padding: 2, mt: 3 }} className='cardStyle'>
                        <Typography variant="h6" className='title' sx={{ mb: 2 }}>Formula Information</Typography>
                        {!formulaInfo ? (
                            <Typography variant="body2" color="text.secondary">
                                {testCode
                                    ? `No formula registered for test code "${testCode}". Ensure the test code matches a registered formula in the backend.`
                                    : 'Enter a test code to see formula information.'}
                            </Typography>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Formula Name</Typography>
                                    <Typography variant="body1" fontWeight={600}>{formulaInfo.name}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Result Unit</Typography>
                                    <Typography variant="body1">{formulaInfo.unit}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Expression</Typography>
                                    <Typography variant="body1" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                        {formulaInfo.expression}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Required Tests (Dependencies)</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                        {formulaInfo.requires.map(code => (
                                            <Chip key={code} label={code} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                </Grid>
                                {formulaInfo.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Notes</Typography>
                                        <Typography variant="body2" color="warning.main">{formulaInfo.notes}</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Paper>
                );
            })()}

            <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mt: 3, justifyContent: { xs: 'center', sm: 'right' } }}>
                <Button className="commonBtn" type='submit' variant='contained' disabled={loading}>{itemData?._id ? "Update" : "Save"}</Button>
                <Button variant='outlined' className='btn cancelBtn' onClick={() => { toggleList() }}>Cancel</Button>
            </Box>
        </Box>
    );
};

export default Form;
